import React from "react";
import { Box } from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import FlashMessage from "../FlashMessage";
import usePushNotifications from "../../hooks/usePushNotifications";
import { isIos, isStandalone } from "../../utils/pwa";
import { C, FONT_BODY } from "../../utils/designTokens";
import { IconTile, RowText, SettingsCard, Switch } from "../Settings/settingsUi";

function Note({ id, children }) {
  return (
    <Box id={id} sx={{ mx: { xs: 2.5, sm: 2.75 }, mb: 2, px: 1.75, py: 1.25, borderRadius: "12px", bgcolor: C.goldSoft, color: C.gold, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, lineHeight: 1.5 }}>
      {children}
    </Box>
  );
}

/**
 * Push notifications for this browser (usePushNotifications). Turning the
 * switch on asks the browser for permission; if that's blocked the switch
 * stays off and says how to allow it.
 */
function NotificationCard() {
  const { supported, permission, subscribed, loading, error, clearError, enable, disable } = usePushNotifications();

  const iosNeedsHomeScreen = isIos() && !isStandalone();
  const blocked = permission === "denied";
  const on = supported && subscribed;
  // Off and can't be turned on from here: no push in this browser, blocked,
  // or an iPhone that needs RushMyFood on the Home Screen first.
  const stuckOff = !on && (!supported || blocked || iosNeedsHomeScreen);

  return (
    <SettingsCard>
      <FlashMessage open={Boolean(error.type)} severity={error.type} alertMessage={error.message} handleClose={clearError} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: { xs: "20px", sm: "22px" } }}>
        <IconTile>
          <NotificationsActiveRoundedIcon />
        </IconTile>
        <RowText titleId="push-title" title="Push notifications" sub="Get notified when your order status changes" />
        <Switch
          checked={on}
          onChange={(next) => (next ? enable() : disable())}
          labelledBy="push-title"
          describedBy={stuckOff ? "push-help" : "push-status"}
          disabled={stuckOff}
          busy={loading}
        />
      </Box>

      {!supported && <Note id="push-help">This browser can't show notifications. Try Chrome, Edge or Safari, or get the RushMyFood app below.</Note>}
      {supported && blocked && (
        <Note id="push-help">
          Notifications are blocked for this site. To allow them, click the icon at the left of the address bar, open the site settings for
          RushMyFood, set Notifications to Allow, then reload this page.
        </Note>
      )}
      {supported && !blocked && iosNeedsHomeScreen && !on && (
        <Note id="push-help">
          On iPhone and iPad, notifications work once RushMyFood is on your Home Screen: tap Share, then "Add to Home Screen", and open it from
          there.
        </Note>
      )}

      <Box
        id="push-status"
        aria-live="polite"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: { xs: "20px", sm: "22px" },
          py: 1.5,
          borderTop: `1px solid ${C.border}`,
          bgcolor: on ? C.greenSoft : C.surface,
          color: on ? C.green : C.muted,
          fontFamily: FONT_BODY,
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, flexShrink: 0, borderRadius: "50%", bgcolor: on ? C.green : "#B9B5AD" }} />
        {on ? "On · you'll get updates for placed, accepted, ready, on the way and delivered" : "Off · you won't get order updates in this browser"}
      </Box>
    </SettingsCard>
  );
}

export default React.memo(NotificationCard);
